import { ProcessesSortByDto, ProcessStatusDto } from '@/api/generated';
import {
  candidateExternalProcessStatusTranslation,
  CandidateExternalProcessStatusTranslation,
} from '@/assets/locales/views/processes/details/list/processCandidatesList.translation.en.ts';
import {
  quickTipsTranslation,
  QuickTipsTranslation,
} from '@/assets/locales/views/processes/list/quickTips/quickTips.translation.en.ts';
import { ProcessesSearchFormModel } from '@/views/processes/list/model/ProcessesSearchForm.model.ts';

export type ProcessStatusesTranslation = Record<ProcessStatusDto, string>;
const statusesTranslation: ProcessStatusesTranslation = {
  [ProcessStatusDto.Active]: 'Active',
  [ProcessStatusDto.Archived]: 'Archived',
  [ProcessStatusDto.AwaitingConfiguration]: 'Awaiting configuration',
  [ProcessStatusDto.Closed]: 'Closed',
  [ProcessStatusDto.Error]: 'Error',
  [ProcessStatusDto.ErrorPendingRestart]: 'Pending restart',
  [ProcessStatusDto.InCreation]: 'In creation',
  [ProcessStatusDto.InProgress]: 'In progress',
};

export type ProcessListTranslation = {
  body: { actions: string; awaitingProcessingCount: string } & Record<ProcessesSortByDto, string>;
  dialog: {
    confirmationRetryProcessContent: string;
  };
  externalProcessStatuses: CandidateExternalProcessStatusTranslation;
  head: Record<keyof ProcessesSearchFormModel, string>;
  labels: {
    activate: string;
    close: string;
    closedProjects: string;
    eRecruiter: string;
    noAwaitingConfigurationProcesses: string;
    noClosedProjects: string;
    retryProcess: string;
    runProcess: string;
    startAnalysis: string;
    startProcess: string;
    unassigned: string;
    view: string;
  };
  notLinked: string;
  possibleStatuses: {
    statuses: Omit<
      ProcessStatusesTranslation,
      | 'AWAITING_CONFIGURATION'
      | 'CREATED'
      | 'ERROR_PENDING_RESTART'
      | 'ERROR'
      | 'IN_CREATION'
      | 'IN_PROGRESS'
    >;
    title: string;
    unhide: string;
  };
  quickTips: QuickTipsTranslation;
  statuses: ProcessStatusesTranslation;
  tour: {
    awaitingConfigurationProcessesButton: {
      text: string;
      title: string;
    };
    closedProcessesButton: {
      text: string;
      title: string;
    };
    createdByFilter: {
      text: string;
      title: string;
    };
    statusesFilter: {
      text: string;
      title: string;
    };
    toggleVisibilityButton: {
      text: string;
      title: string;
    };
  };
};

export const processListTranslation: ProcessListTranslation = {
  body: {
    actions: 'Actions',
    awaitingProcessingCount: 'Awaiting processing',
    [ProcessesSortByDto.CreatedBy]: 'Created by',
    [ProcessesSortByDto.Date]: 'Date',
    [ProcessesSortByDto.ExternalProcessId]: 'eRecruiter ID',
    [ProcessesSortByDto.Name]: 'Name',
    [ProcessesSortByDto.OrdinalNumber]: 'ID',
    [ProcessesSortByDto.Requirements]: 'Requirements',
    [ProcessesSortByDto.Status]: 'Status',
  },
  dialog: {
    confirmationRetryProcessContent:
      'Do you want to restart this process? The operation will retry processing.',
  },
  externalProcessStatuses: candidateExternalProcessStatusTranslation,
  head: {
    createdBy: 'Created by',
    dateFrom: 'Date from',
    dateTo: 'Date to',
    externalProcessId: 'eRecruiter ID',
    ordinalNumber: 'ID',
    searchPhrase: 'Search by name, requirements',
    statuses: 'Status',
  },
  labels: {
    activate: 'Activate',
    close: 'Close',
    closedProjects: 'Closed processes',
    eRecruiter: 'eRecruiter',
    noAwaitingConfigurationProcesses: 'No processes awaiting configuration',
    noClosedProjects: 'No closed processes',
    retryProcess: 'Restart',
    runProcess: 'Run',
    startAnalysis: 'Start analysis',
    startProcess: 'Start process',
    unassigned: 'No owner',
    view: 'View',
  },
  notLinked: 'not linked with eRecruiter',
  possibleStatuses: {
    statuses: {
      [ProcessStatusDto.Active]: 'Activate',
      [ProcessStatusDto.Archived]: 'Archive',
      [ProcessStatusDto.Closed]: 'Close',
    },
    title: 'Change status',
    unhide: 'Unhide',
  },
  quickTips: quickTipsTranslation,
  statuses: statusesTranslation,
  tour: {
    awaitingConfigurationProcessesButton: {
      text: 'To start a new process, click this button to show processes already imported from eRecruiter. Find the process using the "eRecruiter ID" filter and open its details. Then select "Start analysis".',
      title: 'Start a new process',
    },
    closedProcessesButton: {
      text: 'You can now close processes. Closed processes are not shown on the list by default. This button shows how many closed processes you have. Click it to display your closed processes.',
      title: 'Closed processes',
    },
    createdByFilter: {
      text: 'Use this filter to show processes owned by a selected user. Clear it to see all processes, including imported processes awaiting configuration.',
      title: 'Created by Filter',
    },
    statusesFilter: {
      text: 'By default, you can see only active processes. To see all processes, just clear this filter.',
      title: 'Status Filter',
    },
    toggleVisibilityButton: {
      text: 'Use this row action to quickly close or reopen a process without opening process details.',
      title: 'Quick close or reopen',
    },
  },
};
